import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import Layout from '@/components/Layout';

// Pages
import LoginPage from '@/pages/LoginPage';
import DashboardPage from '@/pages/DashboardPage';
import ClientsPage from '@/pages/ClientsPage';
import ClientDetailPage from '@/pages/ClientDetailPage';
import WorkersPage from '@/pages/WorkersPage';
import WorkerDetailPage from '@/pages/WorkerDetailPage';
import ServicesPage from '@/pages/ServicesPage';
import ServiceOrdersPage from '@/pages/ServiceOrdersPage';
import ServiceOrderDetailPage from '@/pages/ServiceOrderDetailPage';
import DocumentsPage from '@/pages/DocumentsPage';
import InvoicesPage from '@/pages/InvoicesPage';
import InvoiceDetailPage from '@/pages/InvoiceDetailPage';
import PaymentsPage from '@/pages/PaymentsPage';
import QuotationsPage from '@/pages/QuotationsPage';
import QuotationDetailPage from '@/pages/QuotationDetailPage';
import CreditNotesPage from '@/pages/CreditNotesPage';
import ExpensesPage from '@/pages/ExpensesPage';
import RemindersPage from '@/pages/RemindersPage';
import ReportsPage from '@/pages/ReportsPage';
import FinancialReportsPage from '@/pages/FinancialReportsPage';
import AnalyticsDashboardPage from '@/pages/AnalyticsDashboardPage';
import GovtServicesPage from '@/pages/GovtServicesPage';
import SettingsPage from '@/pages/SettingsPage';
import CustomerReportsPage from '@/pages/CustomerReportsPage';
import NotificationCenterPage from '@/pages/NotificationCenterPage';
import ExpiryManagementPage from '@/pages/ExpiryManagementPage';
import WalletReportsPage from '@/pages/WalletReportsPage';
import GovtExpensesReportPage from '@/pages/GovtExpensesReportPage';
import ServiceProfitabilityPage from '@/pages/ServiceProfitabilityPage';
import ReportBuilderPage from '@/pages/ReportBuilderPage';
import BulkImportPage from '@/pages/BulkImportPage';
import VisualChartsDashboardPage from '@/pages/VisualChartsDashboardPage';
import RecurringInvoicesPage from '@/pages/RecurringInvoicesPage';
import PaymentRemindersPage from '@/pages/PaymentRemindersPage';

// Protected Route wrapper
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <Layout>{children}</Layout>;
}

// Public route (redirect if logged in)
function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />

      {/* Protected Routes */}
      <Route path="/" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />

      {/* Clients */}
      <Route path="/clients" element={
        <ProtectedRoute>
          <ClientsPage />
        </ProtectedRoute>
      } />
      <Route path="/clients/new" element={
        <ProtectedRoute>
          <ClientDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/clients/:id" element={
        <ProtectedRoute>
          <ClientDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/clients/:id/edit" element={
        <ProtectedRoute>
          <ClientDetailPage />
        </ProtectedRoute>
      } />

      {/* Workers */}
      <Route path="/workers" element={
        <ProtectedRoute>
          <WorkersPage />
        </ProtectedRoute>
      } />
      <Route path="/workers/new" element={
        <ProtectedRoute>
          <WorkerDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/workers/:id" element={
        <ProtectedRoute>
          <WorkerDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/workers/:id/edit" element={
        <ProtectedRoute>
          <WorkerDetailPage />
        </ProtectedRoute>
      } />

      {/* Services */}
      <Route path="/services" element={
        <ProtectedRoute>
          <ServicesPage />
        </ProtectedRoute>
      } />

      {/* Service Orders */}
      <Route path="/service-orders" element={
        <ProtectedRoute>
          <ServiceOrdersPage />
        </ProtectedRoute>
      } />
      <Route path="/service-orders/new" element={
        <ProtectedRoute>
          <ServiceOrderDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/service-orders/:id" element={
        <ProtectedRoute>
          <ServiceOrderDetailPage />
        </ProtectedRoute>
      } />

      {/* Documents */}
      <Route path="/documents" element={
        <ProtectedRoute>
          <DocumentsPage />
        </ProtectedRoute>
      } />

      {/* Invoices */}
      <Route path="/invoices" element={
        <ProtectedRoute>
          <InvoicesPage />
        </ProtectedRoute>
      } />
      <Route path="/invoices/new" element={
        <ProtectedRoute>
          <InvoiceDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/invoices/:id" element={
        <ProtectedRoute>
          <InvoiceDetailPage />
        </ProtectedRoute>
      } />

      {/* Payments */}
      <Route path="/payments" element={
        <ProtectedRoute>
          <PaymentsPage />
        </ProtectedRoute>
      } />
      <Route path="/payments/new" element={
        <ProtectedRoute>
          <PaymentsPage />
        </ProtectedRoute>
      } />

      {/* Quotations */}
      <Route path="/quotations" element={
        <ProtectedRoute>
          <QuotationsPage />
        </ProtectedRoute>
      } />
      <Route path="/quotations/new" element={
        <ProtectedRoute>
          <QuotationDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/quotations/:id" element={
        <ProtectedRoute>
          <QuotationDetailPage />
        </ProtectedRoute>
      } />

      {/* Credit Notes */}
      <Route path="/credit-notes" element={
        <ProtectedRoute>
          <CreditNotesPage />
        </ProtectedRoute>
      } />

      {/* Expenses */}
      <Route path="/expenses" element={
        <ProtectedRoute>
          <ExpensesPage />
        </ProtectedRoute>
      } />

      {/* Wallet Reports */}
      <Route path="/wallet-reports" element={
        <ProtectedRoute>
          <WalletReportsPage />
        </ProtectedRoute>
      } />

      {/* Reminders */}
      <Route path="/reminders" element={
        <ProtectedRoute>
          <RemindersPage />
        </ProtectedRoute>
      } />

      {/* Notification Center */}
      <Route path="/notification-center" element={
        <ProtectedRoute>
          <NotificationCenterPage />
        </ProtectedRoute>
      } />

      {/* Expiry Management */}
      <Route path="/expiry-management" element={
        <ProtectedRoute>
          <ExpiryManagementPage />
        </ProtectedRoute>
      } />

      {/* Reports */}
      <Route path="/reports" element={
        <ProtectedRoute>
          <ReportsPage />
        </ProtectedRoute>
      } />

      {/* Government Expenses Report */}
      <Route path="/govt-expenses-report" element={
        <ProtectedRoute>
          <GovtExpensesReportPage />
        </ProtectedRoute>
      } />

      {/* Customer Comprehensive Reports */}
      <Route path="/customer-reports" element={
        <ProtectedRoute>
          <CustomerReportsPage />
        </ProtectedRoute>
      } />

      {/* Service Profitability Report */}
      <Route path="/service-profitability" element={
        <ProtectedRoute>
          <ServiceProfitabilityPage />
        </ProtectedRoute>
      } />

      {/* Custom Report Builder */}
      <Route path="/report-builder" element={
        <ProtectedRoute>
          <ReportBuilderPage />
        </ProtectedRoute>
      } />

      {/* Bulk Import */}
      <Route path="/bulk-import" element={
        <ProtectedRoute>
          <BulkImportPage />
        </ProtectedRoute>
      } />

      {/* Visual Charts Dashboard */}
      <Route path="/visual-charts" element={
        <ProtectedRoute>
          <VisualChartsDashboardPage />
        </ProtectedRoute>
      } />

      {/* Financial Reports (VAT, P&L) */}
      <Route path="/financial-reports" element={
        <ProtectedRoute>
          <FinancialReportsPage />
        </ProtectedRoute>
      } />

      {/* Business Intelligence & Analytics */}
      <Route path="/analytics" element={
        <ProtectedRoute>
          <AnalyticsDashboardPage />
        </ProtectedRoute>
      } />

      {/* Government Services */}
      <Route path="/govt-services" element={
        <ProtectedRoute>
          <GovtServicesPage />
        </ProtectedRoute>
      } />

      {/* Settings */}
      <Route path="/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />

      <Route path="/recurring-invoices" element={
        <ProtectedRoute>
          <RecurringInvoicesPage />
        </ProtectedRoute>
      } />

      <Route path="/payment-reminders" element={
        <ProtectedRoute>
          <PaymentRemindersPage />
        </ProtectedRoute>
      } />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors closeButton />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
